import React, { type CSSProperties, type FunctionComponentElement } from 'react';

import { useTheme, useForkRef } from '@mui/material';
import { getTransitionProps, reflow } from '@mui/material/transitions/utils';
import { Transition } from 'react-transition-group';
import { type TransitionActions, type TransitionProps, type TransitionStatus } from 'react-transition-group/Transition';

export interface AnimatorProps {
    /**
     * Show the component; triggers the enter or exit states
     */
    in: boolean;

    children: FunctionComponentElement<{}>;

    style?: CSSProperties;

    /**
     * Styles to apply during each stage of the transition
     */
    transitionStyles: { [key in TransitionStatus]?: CSSProperties };

    /**
     * Custom easing provided to the MaterialUI theme
     * `getTransitionProps` function
     */
    easing?: string | { enter?: string; exit?: string };

    /**
     * Whether or not to perform the enter transition
     * when it first mounts, regardless of `in` prop
     */
    appear?: TransitionActions['appear'];

    /**
     * The duration of the transition, in milliseconds.
     */
    timeout?: TransitionProps['timeout'];

    /**
     * Add a custom transition end trigger.
     */
    addEndListener?: TransitionProps['addEndListener'];

    /**
     * Transition callbacks.
     * See react-transition-group documentation for details.
     */
    onExit?: TransitionProps['onExit'];
    onExited?: TransitionProps['onExited'];
    onExiting?: TransitionProps['onExiting'];
    onEnter?: TransitionProps['onEnter'];
    onEntered?: TransitionProps['onEntered'];
    onEntering?: TransitionProps['onEntering'];
}

const Animator: React.FC<AnimatorProps> = React.forwardRef((props, ref) => {
    const {
        in: inProp,
        style,
        easing,
        children,
        onEnter,
        onEntered,
        onEntering,
        onExit,
        onExited,
        onExiting,
        appear = true,
        addEndListener,
        transitionStyles,
        timeout = 220,
        ...other
    } = props;

    const theme = useTheme();

    const nodeRef = React.useRef(null);
    const foreignRef = useForkRef(children.ref, ref);
    const handleRef = useForkRef(nodeRef, foreignRef);

    /**
     * Pull hyphenated, unique CSS properties from the `transitionStyles`
     * prop to use in the DOM element transition styles.
     */
    const getCssPropsToAnimate = (): string[] => {
        const states = Object.keys(transitionStyles);
        let cssProps: string[] = [];

        states.forEach((state) => {
            cssProps = cssProps.concat(Object.keys(transitionStyles[state]));
        });

        const jsProperties = [...new Set(cssProps)];

        const toHyphen = (prop) => prop.replace(/([A-Z])/g, (char) => `-${char[0].toLowerCase()}`);
        return jsProperties.map(toHyphen);
    };

    const normalizedTransitionCallback = (callback) => (maybeIsAppearing) => {
        if (!callback) return;

        const node = nodeRef.current;

        // onEnter() and onExit() callbacks have a different arguments.length value.
        if (maybeIsAppearing === undefined) {
            callback(node);
        } else {
            callback(node, maybeIsAppearing);
        }
    };

    const handleEntering = normalizedTransitionCallback(onEntering);

    const handleEnter = normalizedTransitionCallback((node, isAppearing) => {
        reflow(node); // So the animation always start from the start.

        const transitionProps = getTransitionProps({ style, timeout, easing }, { mode: 'enter' });
        node.style.transition = theme.transitions.create(getCssPropsToAnimate(), transitionProps);

        if (onEnter) {
            onEnter(node, isAppearing);
        }
    });

    const handleEntered = normalizedTransitionCallback(onEntered);

    const handleExiting = normalizedTransitionCallback(onExiting);

    const handleExit = normalizedTransitionCallback((node) => {
        const transitionProps = getTransitionProps({ style, timeout, easing }, { mode: 'exit' });
        node.style.transition = theme.transitions.create(getCssPropsToAnimate(), transitionProps);

        if (onExit) {
            onExit(node);
        }
    });

    const handleExited = normalizedTransitionCallback(onExited);

    return (
        <Transition
            appear={appear}
            in={inProp}
            nodeRef={nodeRef}
            onEnter={handleEnter}
            onEntered={handleEntered}
            onEntering={handleEntering}
            onExit={handleExit}
            onExited={handleExited}
            onExiting={handleExiting}
            timeout={timeout}
            {...other}
        >
            {(state, childProps) =>
                React.cloneElement(children, {
                    style: {
                        visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
                        ...transitionStyles[state],
                        ...style,
                        ...children.props.style
                    },
                    ref: handleRef,
                    ...childProps
                })
            }
        </Transition>
    );
});

export default Animator;
